import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import RewardProgramSelector from 'components/crucible/detail/rewards/RewardProgramSelector';
import { useRewardPrograms } from 'store/zustand/useRewardPrograms';
import { useHistory, useLocation } from 'react-router-dom';
import { useWeb3React } from 'hooks/web3';
import { useLeaderboard } from 'hooks/query/useLeaderboard';
import { truncate } from 'utils/address';
import { AiOutlineEye } from 'react-icons/ai';
import { useModal } from 'store/modals';
import { ModalType } from 'components/modals/types';
import { FiExternalLink } from 'react-icons/fi';
import { usePrograms } from 'hooks/query/usePrograms';
import { EtherscanLinkType, getEtherscanLink } from 'utils/getEtherscanLink';
import { BiInfoCircle } from 'react-icons/bi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { config } from 'store/initialisers/config';
import { Crucible } from 'types';

dayjs.extend(relativeTime);

type Props = {
  rewardProgramAddress: string;
  embedded?: boolean;
  hideHeading?: boolean;
  hideProgramSelector?: boolean;
  selectedCrucible?: Crucible;
};

export const LeaderboardTable: FC<Props> = ({
  embedded,
  rewardProgramAddress,
  hideHeading,
  hideProgramSelector,
  selectedCrucible
}) => {
  const { openModal } = useModal();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(window.location.search);
  const network = params.get('network');

  const { chainId: signerChainId = 1, account } = useWeb3React();
  const { data: extendedRewardPrograms } = usePrograms();
  const [search, setSearch] = useState('');

  const chainId = network ? Number(network) : signerChainId;

  const { crucibleFactoryAddress } = config[Number(chainId)];

  const { currentRewardProgram, setCurrentRewardProgram, rewardPrograms } =
    useRewardPrograms();

  useEffect(() => {
    // check if url is different than current reward program
    if (
      currentRewardProgram.address.toLowerCase() !==
      rewardProgramAddress.toLowerCase()
    ) {
      //check if reward program exists
      const rewardProgramFromParam = rewardPrograms.find(
        (p) => p.address.toLowerCase() === rewardProgramAddress.toLowerCase()
      );

      // if exists, set as current program
      if (rewardProgramFromParam) {
        setCurrentRewardProgram(chainId, rewardProgramFromParam);
      } else {
        if (!embedded) {
          history.push({
            pathname: `/leaderboard/${currentRewardProgram.address}`,
            search: location.search
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardProgramAddress, chainId, currentRewardProgram]);

  const { data } = useLeaderboard(
    chainId,
    currentRewardProgram.address,
    search
  );

  const stakingToken = useMemo(() => {
    if (extendedRewardPrograms) {
      const matchingRewardProgram = extendedRewardPrograms.find(
        (p) =>
          p.rewardProgramAddress.toLowerCase() ===
          rewardProgramAddress.toLowerCase()
      );
      if (matchingRewardProgram) {
        return matchingRewardProgram.rewardProgramTokens.stakingToken;
      }
    }
  }, [extendedRewardPrograms, rewardProgramAddress]);

  const renderMedal = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇 ';
      case 2:
        return '🥈 ';
      case 3:
        return '🥉 ';
      default:
        return '#' + rank;
    }
  };

  const getBackgroundColor = (d: any) => {
    if (embedded) {
      return d.crucibleAddress.toLowerCase() ===
        selectedCrucible?.id.toLowerCase()
        ? 'gray.700'
        : 'gray.800';
    } else {
      return d.owner.toLowerCase() === account?.toLowerCase()
        ? 'gray.800'
        : 'gray.900';
    }
  };

  return (
    <Box maxW='5xl' mx='auto'>
      {!hideHeading && (
        <Box my={8}>
          <Heading size='xl' textAlign='center' mb={2}>
            Leaderboard - Top 100
          </Heading>
          {data && data.lastUpdated && !embedded && (
            <Text textAlign='center' color='gray.500'>
              {data.maxRank} participants. Updated{' '}
              {dayjs(dayjs.unix(Number(data.lastUpdated))).fromNow()}
            </Text>
          )}
        </Box>
      )}
      {!hideProgramSelector && (
        <RewardProgramSelector
          isReadOnly={false}
          isLeaderboard={true}
          crucible={selectedCrucible}
        />
      )}
      {data && data.lastUpdated && embedded && (
        <Text
          textAlign={['center', 'right']}
          fontSize={'sm'}
          color='gray.500'
          position={'absolute'}
          top={-5}
          mx={['auto', '0']}
          right={[0, 5]}
          left={[0]}
        >
          {data.maxRank} participants. Updated{' '}
          {dayjs(dayjs.unix(Number(data.lastUpdated))).fromNow()}
        </Text>
      )}
      <Box
        sx={{
          border: '1px solid gray',
          mt: embedded ? 0 : 8
        }}
        borderRadius={'2xl'}
      >
        <Box>
          <Box p={4}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search crucible address'
              variant='filled'
            />
            {data?.leaderboard.length === 0 && (
              <Text mt={4}>
                Could not find an entry for this crucible address
              </Text>
            )}
          </Box>
          <Divider />
        </Box>
        <TableContainer borderRadius={'2xl'}>
          <Table>
            <Thead>
              <Tr sx={{ '& th': { px: 1, textAlign: 'center' } }}>
                <Th>Rank</Th>
                <Th display={['none', 'table-cell']}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                      label='Points are a weighted calculation using total staked duration (0.3x) and total staked amount (0.1x).'
                      placement='bottom'
                      hasArrow
                    >
                      <HStack>
                        <span>points</span>
                        <BiInfoCircle size={16} />
                      </HStack>
                    </Tooltip>
                  </Box>
                </Th>
                <Th>Crucible</Th>
                <Th display={['none', 'none', 'table-cell']}>Owner</Th>
                <Th display={['none', 'table-cell']}>Total days</Th>
                <Th>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                      label='Average balance for the duration.'
                      placement='bottom'
                      hasArrow
                    >
                      <HStack>
                        {embedded ? (
                          <Text>avg. stake</Text>
                        ) : (
                          <>
                            <Text isTruncated maxW={'100px'}>
                              {stakingToken?.tokenSymbol}
                            </Text>{' '}
                            <BiInfoCircle size={16} />
                          </>
                        )}
                      </HStack>
                    </Tooltip>
                  </Box>
                </Th>
                <Th>Rewards</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.leaderboard.length > 0 &&
                data.leaderboard.map((d: any, idx: number) => (
                  <Tr
                    key={d.crucibleAddress}
                    sx={{
                      '& td': { px: 1, textAlign: 'center' },
                      background: getBackgroundColor(d)
                    }}
                  >
                    <Td px={3}>{renderMedal(d.rank)}</Td>
                    <Td display={['none', 'table-cell']}>
                      <Tooltip label={d.level} placement='bottom' hasArrow>
                        {d.level < 1 ? d.level.toFixed(5) : d.level.toFixed(2)}
                      </Tooltip>
                    </Td>
                    <Td>
                      {d.factory.toLowerCase() ===
                      crucibleFactoryAddress.toLowerCase() ? (
                        <Link
                          href={`/explore/${d.crucibleAddress}?network=${chainId}`}
                          isExternal
                        >
                          <HStack justifyContent='center'>
                            <span>{truncate(d.crucibleAddress)}</span>
                            <FiExternalLink />
                          </HStack>
                        </Link>
                      ) : (
                        <Tooltip
                          label={'Ampleforth UniveralVault - Unsupported'}
                          placement='bottom'
                          hasArrow
                        >
                          <Text color={'gray.400'}>
                            {truncate(d.crucibleAddress)}
                          </Text>
                        </Tooltip>
                      )}
                    </Td>
                    <Td display={['none', 'none', 'table-cell']} isTruncated>
                      <Link
                        href={getEtherscanLink(
                          chainId,
                          d.owner,
                          EtherscanLinkType.ADDRESS
                        )}
                        isExternal
                      >
                        <HStack justifyContent='center'>
                          <span>{d.ens || truncate(d.owner)}</span>
                          <FiExternalLink />
                        </HStack>
                      </Link>
                    </Td>
                    <Td display={['none', 'table-cell']}>
                      {d.totalDuration.toFixed(0)}
                    </Td>
                    <Td cursor='pointer'>
                      <Tooltip
                        label={d.totalAmount + ' ' + stakingToken?.tokenSymbol}
                        placement='bottom'
                        hasArrow
                      >
                        {d.totalAmount < 1
                          ? d.totalAmount.toFixed(5)
                          : d.totalAmount.toFixed(2)}
                      </Tooltip>
                    </Td>
                    <Td>
                      <Tooltip
                        label={
                          d.rewardsArray.length === 0
                            ? 'No rewards claimed'
                            : 'View claimed rewards'
                        }
                        placement='bottom'
                        hasArrow
                      >
                        <span>
                          <IconButton
                            aria-label='view'
                            variant='ghost'
                            disabled={d.rewardsArray.length === 0}
                            icon={<AiOutlineEye fontSize={20} />}
                            onClick={() =>
                              openModal(ModalType.leaderboardRewards, {
                                rewards: d.rewardsArray
                              })
                            }
                          />
                        </span>
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
